import CheckIcon from '@mui/icons-material/Check'
import WarningIcon from '@mui/icons-material/Warning'
import {
  Drawer,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button as MUIButton,
  Toolbar as MUIToolbar,
  Step,
  StepButton,
  StepContent,
  StepLabel,
  Stepper,
  Theme,
  useMediaQuery,
} from '@mui/material'
import clsx from 'clsx'
import React from 'react'
import {
  FormProps,
  LinearProgress,
  Loading,
  SaveButton,
  useGetList,
  useLocale,
  useSidebarState,
  useTranslate,
} from 'react-admin'
import { useFormContext, useWatch } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { makeStyles } from 'tss-react/mui'
import {
  CategoryEntryIcon,
  UpsertBoat,
  UpsertBoatSection,
  WizardForm,
  WizardFormProgressProps,
  WizardToolbarProps,
} from '~/components'
import { Sections } from '~/graphql/generated'
import { GUIDED_FORM_ID, categoryEntryState, guidedFormRoot } from '~/helpers'
import { WizardFormStep } from '~/vendor/ra-form-layout'

const drawerWidth = 240

const useStyles = makeStyles()((theme) => ({
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  drawerOpen: {
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerClose: {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: theme.spacing(7) + 1,
  },
}))

const ProgressStepper = ({
  currentStep,
  onStepClick,
  steps,
  invalid,
  ...rest
}: WizardFormProgressProps) => {
  const { classes } = useStyles()
  const [open, setSidebarOpen] = useSidebarState()
  const values = useWatch()
  const translate = useTranslate()
  const locale = useLocale()

  const handleStepClick = (index: number) => (): void => onStepClick?.(index)

  const isXSmall = useMediaQuery<Theme>((theme) => theme.breakpoints.down('xs'))

  const dispatch = useDispatch()
  const closeSidebar = () => setSidebarOpen(false)

  const stepsStates = React.useMemo(() => {
    return (steps ?? []).map((step, index) => {
      const { checkFields, section } = step.props
      const sectionValues = section
        ? values.boat_section?.find((bs) => bs.section_id === section.id)
        : null
      const missingFields = checkFields ? checkFields.filter((field) => !values?.[field]) : []
      const categoriesStates = section
        ? section.categories.map((category) => {
            const categoryValues = sectionValues?.boat_section_category?.find(
              (bsc) => bsc.category_id === category.id
            )
            return categoryEntryState(categoryValues, category)
          })
        : null
      const completed = sectionValues && categoriesStates?.every((cState) => cState.checked)

      return { section, sectionValues, checkFields, missingFields, categoriesStates, completed }
    })
  }, [steps, values])

  return (
    <Drawer
      variant={isXSmall ? 'temporary' : 'permanent'}
      slotProps={{ paper: { style: { borderTop: 'none' } } }}
      className={clsx(classes.drawer, {
        [classes.drawerOpen]: open,
        [classes.drawerClose]: !open,
      })}
      classes={{
        paper: clsx({
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
        }),
      }}
      open={isXSmall ? open : true}
      onClose={closeSidebar}
    >
      <Stepper
        activeStep={currentStep}
        {...rest}
        style={{ marginTop: '48px', border: 'none' }}
        orientation='vertical'
        nonLinear
      >
        {(steps ?? []).map((step, index) => {
          const label = React.cloneElement(step, { intent: 'label' })
          const { section, checkFields, missingFields, categoriesStates, completed } =
            stepsStates[index]

          return (
            <Step key={`step_${index}`} completed={completed}>
              {!onStepClick || invalid ? (
                <StepLabel error={missingFields?.length > 0}>{label}</StepLabel>
              ) : (
                <StepButton onClick={handleStepClick(index)} tabIndex={-1}>
                  <StepLabel error={missingFields?.length > 0}>{label}</StepLabel>
                </StepButton>
              )}
              {checkFields && (
                <StepContent>
                  <List dense disablePadding>
                    {checkFields.map((field) => (
                      <ListItem disableGutters key={`step_${index}_check_${field}`}>
                        <ListItemIcon>
                          {values?.[field] ? <CheckIcon /> : <WarningIcon />}
                        </ListItemIcon>
                        <ListItemText primary={translate(`resources.boats.fields.${field}`)} />
                      </ListItem>
                    ))}
                  </List>
                </StepContent>
              )}
              {section && (
                <StepContent>
                  <List dense disablePadding>
                    {section.categories.map((category, cIndex) => {
                      const { categoryValues, attributesState } = categoriesStates[cIndex]

                      return (
                        <ListItem
                          disableGutters
                          sx={{ opacity: categoryValues ? 1 : 0.5 }}
                          key={`step_${index}_category_${category.id}`}
                        >
                          <ListItemIcon>
                            <CategoryEntryIcon values={categoryValues} state={attributesState} />
                          </ListItemIcon>
                          <ListItemText
                            primary={category.translated_name?.[locale] || category.name}
                          />
                        </ListItem>
                      )
                    })}
                  </List>
                </StepContent>
              )}
            </Step>
          )
        })}
      </Stepper>
    </Drawer>
  )
}

const GuidedBoatSectionForm = ({ section }: { section: Sections }) => {
  const locale = useLocale()
  const { setValue, getValues } = useFormContext()
  const values = getValues()
  values.boat_section ||= []
  const searchIdx = values.boat_section.findIndex((bs) => bs.section_id === section.id)
  const formDataIdx =
    searchIdx >= 0 ? searchIdx : values.boat_section.push({ section_id: section.id }) - 1
  const getSource = (scopedSource: string) => `boat_section[${formDataIdx}].${scopedSource}`

  return (
    <>
      {section.translated_name?.[locale]}
      <UpsertBoatSection
        section={section}
        formData={values}
        source={getSource('boat_section_category')}
        guided
        showNotes
      />
    </>
  )
}

const GuidedToolbar = ({
  hasPreviousStep,
  hasNextStep,
  onPreviousClick,
  onNextClick,
  invalid,
}: WizardToolbarProps) => {
  // react-admin 5's <SaveButton> submits the surrounding form itself, so the
  // old handleSubmitWithRedirect/redirect/saving plumbing is gone.

  return (
    <MUIToolbar>
      <Grid
        container
        direction='row'
        sx={{ justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Grid>
          {hasPreviousStep ? (
            <MUIButton variant='text' onClick={onPreviousClick}>
              Previous{/* {translate('ra-form-layout.action.previous')} */}
            </MUIButton>
          ) : null}
        </Grid>
        <Grid>
          {hasNextStep ? (
            <MUIButton disabled={invalid} variant='contained' color='primary' onClick={onNextClick}>
              Next{/* {translate('ra-form-layout.action.next')} */}
            </MUIButton>
          ) : (
            <SaveButton disabled={invalid} alwaysEnable={false} />
          )}
        </Grid>
      </Grid>
    </MUIToolbar>
  )
}

const GuidedBoatForm = (props: Omit<FormProps, 'render'>) => {
  const { pathname } = useLocation()
  const formRoot = guidedFormRoot(pathname)
  const locale = useLocale()
  const { data: sections, isLoading } = useGetList<Sections>('sections', {
    pagination: { page: 1, perPage: 25 },
    sort: { field: 'position', order: 'ASC' },
  })
  const { classes } = useStyles()
  const [open, setSidebarOpen] = useSidebarState()

  return isLoading ? (
    <>
      <Drawer
        variant='permanent'
        slotProps={{ paper: { style: { zIndex: 1400 } } }}
        className={clsx(classes.drawer, {
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
        })}
        classes={{
          paper: clsx({
            [classes.drawerOpen]: open,
            [classes.drawerClose]: !open,
          }),
        }}
      >
        <LinearProgress />
      </Drawer>
      <Loading />
    </>
  ) : (
    <WizardForm
      id={GUIDED_FORM_ID}
      {...props}
      progress={ProgressStepper}
      toolbar={GuidedToolbar}
      formRootPathname={formRoot}
      syncWithLocation
    >
      <WizardFormStep label='resources.boats.base' checkFields={['name', 'model']}>
        <UpsertBoat {...props} />
      </WizardFormStep>
      {(sections ?? []).map((section) => (
        <WizardFormStep
          label={section.translated_name?.[locale]}
          section={section}
          key={section.slug}
        >
          <GuidedBoatSectionForm section={section} />
        </WizardFormStep>
      ))}
    </WizardForm>
  )
}

export default GuidedBoatForm
